import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  CardActionArea
} from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Link, useLocation } from 'react-router-dom'

import Content from 'components/common/Content'
import Icon from 'components/Icon'
import Header from 'components/navigation/Header'
import useAuth from 'hooks/use-auth'
import { colors } from 'mui-theme/theme'
import theme from 'theme'

export interface HelpContentItem {
  title: string
  description: string
  url: string
  show: boolean
  target?: string
}

export function getHelpContent(
  disableForOnboarding,
  hasFaultInjection,
  isTrial,
  isPaywallActive,
  tier
): HelpContentItem[] {
  return [
    {
      title: 'Documentation',
      description:
        'Reliability is mission critical at any organization building Internet-scale applications. Access the documentation on how to measure, prioritize, and track improvements.',
      url: 'https://www.gremlin.com/docs/?ref=app',
      target: '_blank',
      show: true
    },
    {
      title: 'Gremlin User Community Slack',
      description:
        'An online support community to discuss topics in the reliability management space. Find references to help trouble shoot setup and management of your account.',
      url: 'https://communityinviter.com/apps/gremlincommun-03b6470/gremlinusercommunity',
      target: '_blank',
      show: true
    },
    {
      title: 'Join a Workshop',
      description:
        'See Gremlin in action as one of our experts guides you through the platform in our weekly, live demo. You will also have an opportunity to participate during the interactive Q&A segment.',
      url: 'https://www.gremlin.com/weekly-live-demo/?ref=app',
      target: '_blank',
      show: true
    },
    {
      title: 'API',
      description:
        'API Documentation specifically designed for Fault Injection accounts to support scaling your applications.',
      url: '/api',
      show: !disableForOnboarding && hasFaultInjection
    },
    {
      title: 'Knowledge Base',
      description:
        'Access frequently asked questions and in-depth test and error explanations.',
      url: 'https://support-site.gremlin.com/support/solutions',
      target: '_blank',
      show: true
    },
    {
      title: 'File a Support Ticket',
      description:
        'Access an online form to submit support tickets to address any current issues you are having with your account.',
      url: 'https://support-site.gremlin.com/support/tickets/new',
      target: '_blank',
      show: (isTrial && tier === 'Enterprise') || (!isTrial && !isPaywallActive)
    }
  ]
}

export const HelpPage: React.FC = () => {
  const { isTrial, isPaywallActive, hasFaultInjection, tier } = useAuth()
  const { pathname: currentPathname } = useLocation()
  const disableForOnboarding =
    currentPathname.startsWith('/onboarding') &&
    currentPathname !== '/onboarding/profile'

  const helpContent: HelpContentItem[] = getHelpContent(
    disableForOnboarding,
    hasFaultInjection,
    isTrial,
    isPaywallActive,
    tier
  )

  return (
    <>
      <Helmet title="Help" />
      <Header>
        <Breadcrumbs aria-label="breadcrumb">
          <Stack alignItems="center" direction="row">
            <Icon
              css={{ marginRight: theme.space[2] }}
              icon="messageQuestion"
            />{' '}
            <Typography color="text.primary">Help</Typography>
          </Stack>
        </Breadcrumbs>
      </Header>
      <Content
        css={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridAutoRows: 'min-content',
          gap: theme.space[6]
        }}
      >
        <Grid container data-test-help-page-card-items direction="row" gap={5}>
          {helpContent.map(
            (contentItem) =>
              contentItem.show && (
                <Grid
                  key={contentItem.title}
                  item
                  lg={3}
                  md={5}
                  sm={12}
                  xs={12}
                >
                  <Link
                    css={{ textDecoration: 'none' }}
                    data-test-help-page-link={contentItem.url}
                    target={contentItem.target}
                    to={{ pathname: contentItem.url }}
                  >
                    <CardActionArea css={{ height: '100%' }}>
                      <Card
                        css={{ height: '100%' }}
                        data-test-help-page-card={contentItem.title}
                      >
                        <CardHeader
                          title={
                            <Typography
                              data-test-help-page-card-header-text={
                                contentItem.title
                              }
                              variant="h5"
                            >
                              {contentItem.title}
                              {contentItem.target && (
                                <Icon
                                  color={colors.secondary.blue[600]}
                                  css={{
                                    fontSize: theme.fontSizes[2],
                                    marginLeft: theme.space[1]
                                  }}
                                  icon="arrowUpRightFromSquare"
                                />
                              )}
                            </Typography>
                          }
                        />
                        <CardContent>
                          <Typography
                            data-test-help-card-description
                            variant="body2"
                          >
                            {contentItem.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </CardActionArea>
                  </Link>
                </Grid>
              )
          )}
        </Grid>
      </Content>
    </>
  )
}
